import { Type } from "@sinclair/typebox";
import { IdSchema, Sha256Schema, TimestampSchema } from "./shared.js";
export const FileRefSchema = Type.Object({ fileId: IdSchema }, { additionalProperties: false });
export const FileTreePathSchema = Type.String({
    minLength: 1,
    maxLength: 4096,
    pattern: "^(?!.*[\\\\\\u0000-\\u001f\\u007f])(?![.])[^/]{1,255}(?:/(?![.])[^/]{1,255}){0,127}$",
});
export const FileTreeReferenceSchema = Type.Object({
    files: Type.Array(Type.Object({ path: FileTreePathSchema, file: FileRefSchema }, { additionalProperties: false }), { maxItems: 256 }),
    directories: Type.Array(FileTreePathSchema, { maxItems: 256, uniqueItems: true }),
    entrypoint: Type.Union([FileTreePathSchema, Type.Null()]),
}, { additionalProperties: false });
export const SessionFilePathSchema = Type.String({
    minLength: 1,
    maxLength: 4096,
    pattern: "^(?!.*[\\\\\\u0000-\\u001f\\u007f])(input|result|src|tests)/(?![.])[^/]+(?:/(?![.])[^/]+)*$",
});
export const FileFormatSchema = Type.String({ pattern: "^[a-z][a-z0-9._+-]{0,63}$" });
export const FileSourceSchema = Type.Union([
    Type.Object({ kind: Type.Literal("demo"), caseId: IdSchema, caseDigest: Sha256Schema, assetId: IdSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("upload") }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("verified_existing") }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("observation"),
        observationId: IdSchema,
        actionId: IdSchema,
        artifactId: IdSchema,
    }, { additionalProperties: false }),
]);
export const FileVersionSchema = Type.Object({
    fileId: IdSchema,
    identityId: IdSchema,
    sessionId: IdSchema,
    path: SessionFilePathSchema,
    digest: Sha256Schema,
    sizeBytes: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    mediaType: Type.String({ minLength: 1, maxLength: 255 }),
    format: Type.Union([FileFormatSchema, Type.Null()]),
    source: FileSourceSchema,
    registeredAt: TimestampSchema,
}, { additionalProperties: false });
const FileInputPointerSchema = Type.String({
    minLength: 1,
    maxLength: 1024,
    pattern: "^(?:/(?:[^~/]|~[01])*)+$",
});
const AcceptedFileFormatsSchema = Type.Array(FileFormatSchema, {
    minItems: 1,
    maxItems: 64,
    uniqueItems: true,
});
export const FileInputBindingSchema = Type.Object({
    pointer: FileInputPointerSchema,
    itemPointer: Type.Optional(FileInputPointerSchema),
    optionalItem: Type.Optional(Type.Literal(true)),
    formats: AcceptedFileFormatsSchema,
    memberFormats: Type.Optional(Type.Array(Type.Object({
        pathsPointer: FileInputPointerSchema,
        formats: AcceptedFileFormatsSchema,
    }, { additionalProperties: false }), { minItems: 1, maxItems: 16 })),
    cardinality: Type.Union([Type.Literal("single"), Type.Literal("list"), Type.Literal("tree")]),
    maximumBytes: Type.Integer({ minimum: 1, maximum: 20 * 1024 ** 3 }),
    maximumFiles: Type.Integer({ minimum: 1, maximum: 256 }),
}, { additionalProperties: false });
export const FileInputFormatIssueSchema = Type.Object({
    pointer: FileInputBindingSchema.properties.pointer,
    index: Type.Integer({ minimum: 0, maximum: 255 }),
    fileRef: FileRefSchema,
    digest: Sha256Schema,
    actualFormat: Type.Union([FileFormatSchema, Type.Null()]),
    acceptedFormats: FileInputBindingSchema.properties.formats,
}, { additionalProperties: false });
export const FileConversionDirectionSchema = Type.Object({ sourceFormat: FileFormatSchema, targetFormat: FileFormatSchema }, { additionalProperties: false });
export const FileOutputBindingSchema = Type.Object({
    pointer: FileInputBindingSchema.properties.pointer,
    cardinality: Type.Union([Type.Literal("single"), Type.Literal("list")]),
    itemPointer: Type.Optional(FileInputBindingSchema.properties.pointer),
    optionalItem: Type.Optional(Type.Literal(true)),
    maximumBytes: FileInputBindingSchema.properties.maximumBytes,
    // 远端声明上限与平台单动作产物上限分别校验。
    maximumFiles: Type.Integer({ minimum: 1, maximum: 4096 }),
}, { additionalProperties: false });
export const FileIoContractSchema = Type.Object({
    inputs: Type.Array(FileInputBindingSchema, { maxItems: 256 }),
    outputs: Type.Array(FileOutputBindingSchema, { maxItems: 256 }),
    preparation: Type.Optional(Type.Union([
        Type.Object({ kind: Type.Literal("inspect"), profileDigest: Sha256Schema }, { additionalProperties: false }),
        Type.Object({
            kind: Type.Literal("convert"),
            engine: Type.String({
                minLength: 1,
                maxLength: 128,
                pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
            }),
            profileDigest: Sha256Schema,
            sourceFormatPointer: FileInputBindingSchema.properties.pointer,
            targetFormatPointer: FileInputBindingSchema.properties.pointer,
            directions: Type.Array(FileConversionDirectionSchema, {
                minItems: 1,
                maxItems: 64,
                uniqueItems: true,
            }),
        }, { additionalProperties: false }),
    ])),
}, { additionalProperties: false });
const FileContentSchema = Type.Object({
    digest: Sha256Schema,
    sizeBytes: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    format: FileFormatSchema,
}, { additionalProperties: false });
export const FilePreparationResultSchema = Type.Union([
    Type.Object({
        kind: Type.Literal("inspect"),
        profileDigest: Sha256Schema,
        source: FileContentSchema,
    }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("convert"),
        profileDigest: Sha256Schema,
        source: FileContentSchema,
        output: Type.Object({
            ...FileContentSchema.properties,
            path: Type.Union([FileRefSchema, Type.String({ minLength: 1, maxLength: 4096 })]),
        }, { additionalProperties: false }),
    }, { additionalProperties: false }),
]);
const FilePreparationFileSchema = Type.Pick(FileVersionSchema, [
    "fileId",
    "path",
    "digest",
    "sizeBytes",
    "format",
]);
const FilePreparationOutcomeFields = {
    actionId: IdSchema,
    observationId: IdSchema,
    resultDigest: Sha256Schema,
    functionName: Type.String({ minLength: 1, maxLength: 256 }),
    functionSchemaDigest: Sha256Schema,
    profileDigest: Sha256Schema,
    kind: Type.Union([Type.Literal("inspect"), Type.Literal("convert")]),
    source: FilePreparationFileSchema,
    targetFormat: Type.Union([FileFormatSchema, Type.Null()]),
};
export const FilePreparationOutcomeSchema = Type.Union([
    Type.Object({
        ...FilePreparationOutcomeFields,
        status: Type.Literal("succeeded"),
        output: Type.Object({ ...FilePreparationFileSchema.properties, format: FileFormatSchema }, { additionalProperties: false }),
    }, { additionalProperties: false }),
    Type.Object({
        ...FilePreparationOutcomeFields,
        status: Type.Literal("failed"),
        output: Type.Null(),
    }, { additionalProperties: false }),
]);
export const FileInputManifestSchema = Type.Object({
    identityId: IdSchema,
    sessionId: IdSchema,
    generation: Type.Integer({ minimum: 0 }),
    targetDigest: Sha256Schema,
    functionSchemaDigest: Sha256Schema,
    argumentsDigest: Sha256Schema,
    files: Type.Array(Type.Object({
        pointer: Type.String({ minLength: 1, maxLength: 1024 }),
        index: Type.Integer({ minimum: 0, maximum: 255 }),
        fileId: IdSchema,
        path: SessionFilePathSchema,
        treePath: Type.Optional(FileTreePathSchema),
        digest: Sha256Schema,
        sizeBytes: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
        format: Type.Union([FileFormatSchema, Type.Null()]),
    }, { additionalProperties: false }), { maxItems: 256 }),
    trees: Type.Optional(Type.Array(Type.Object({
        pointer: FileInputBindingSchema.properties.pointer,
        directories: FileTreeReferenceSchema.properties.directories,
        entrypoint: FileTreeReferenceSchema.properties.entrypoint,
    }, { additionalProperties: false }), { minItems: 1, maxItems: 256 })),
}, { additionalProperties: false });
const InputTreeDestinationSchema = Type.String({ pattern: "^input/trees/[a-f0-9]{64}$" });
export const InputTreePlanSchema = Type.Object({
    destination: InputTreeDestinationSchema,
    files: Type.Array(Type.Object({
        source: SessionFilePathSchema,
        path: FileTreePathSchema,
        sizeBytes: Type.Integer({ minimum: 0, maximum: 20 * 1024 ** 3 }),
        digest: Sha256Schema,
    }, { additionalProperties: false }), { maxItems: 256 }),
    directories: FileTreeReferenceSchema.properties.directories,
    maximumBytes: Type.Integer({ minimum: 0, maximum: 20 * 1024 ** 3 }),
}, { additionalProperties: false });
export const InputTreeMaterializedSchema = Type.Object({
    requestDigest: Sha256Schema,
    destination: InputTreeDestinationSchema,
    sizeBytes: Type.Integer({ minimum: 0, maximum: 20 * 1024 ** 3 }),
    fileCount: Type.Integer({ minimum: 0, maximum: 256 }),
}, { additionalProperties: false });
export const ToolInputBindingSchema = Type.Object({
    grantId: IdSchema,
    actionRequestDigest: Sha256Schema,
    inputManifestDigest: Sha256Schema,
}, { additionalProperties: false });
export const ToolInputUploadSchema = Type.Object({
    ...ToolInputBindingSchema.properties,
    fileId: IdSchema,
    attempt: Type.Integer({ minimum: 1, maximum: 3 }),
}, { additionalProperties: false });
export const ToolInputReceiptSchema = Type.Object({
    ...ToolInputUploadSchema.properties,
    sizeBytes: Type.Integer({ minimum: 0, maximum: 20 * 1024 ** 3 }),
    digest: Sha256Schema,
}, { additionalProperties: false });
export const ToolInputStateSchema = Type.Object({
    ...ToolInputBindingSchema.properties,
    state: Type.Union([
        Type.Literal("awaiting_inputs"),
        Type.Literal("committed"),
        Type.Literal("cancelled"),
    ]),
    files: Type.Array(Type.Object({
        fileId: IdSchema,
        state: Type.Union([
            Type.Literal("missing"),
            Type.Literal("receiving"),
            Type.Literal("verified"),
        ]),
        attempts: Type.Integer({ minimum: 0, maximum: 3 }),
        leaseExpiresAt: Type.Union([TimestampSchema, Type.Null()]),
    }, { additionalProperties: false }), { maxItems: 256 }),
}, { additionalProperties: false });
//# sourceMappingURL=files.js.map